import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import {
  getSubstanceFull,
  upsertSubstanceProfile,
  upsertSubstanceRoute, deleteSubstanceRoute,
  upsertSubstanceEffect, deleteSubstanceEffect,
} from "@/lib/admin-substances.functions";
import { ROA_TYPES, EFFECT_KINDS, CONFIDENCE_LEVELS } from "@/lib/admin-substances.schemas";

export const Route = createFileRoute("/_authenticated/_admin/admin/substances/$id/edit")({
  component: SubstanceEditor,
});

const input: React.CSSProperties = {
  width: "100%", background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border-purple)", borderRadius: 6,
  padding: "0.4rem 0.55rem", color: "var(--text-primary)", fontSize: "0.85rem",
};
const label: React.CSSProperties = {
  display: "block", color: "var(--text-secondary)", fontSize: "0.72rem",
  textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
};
const section: React.CSSProperties = {
  background: "var(--bg-surface)", border: "1px solid var(--border-purple)",
  borderRadius: 8, padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.9rem",
};
const btn: React.CSSProperties = {
  padding: "0.4rem 0.85rem", background: "var(--accent-cyan)", color: "#0f0a1a",
  border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem",
};

function SubstanceEditor() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const fetchFull = useServerFn(getSubstanceFull);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-substance-full", id],
    queryFn: () => fetchFull({ data: { id } }),
  });

  if (isLoading) return <p style={{ color: "var(--text-muted)", padding: "2rem" }}>Lade…</p>;
  if (!data) return null;

  return (
    <div style={{ maxWidth: 900, margin: "2rem auto", padding: "0 1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <Link to="/admin" style={{ color: "var(--accent-cyan)", textDecoration: "none", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 4 }}>
        <ArrowLeft size={14} /> Zurueck
      </Link>
      <h1 style={{ color: "var(--accent-cyan)", margin: 0 }}>{data.substance.name}</h1>

      <ProfileForm substanceId={id} initial={data.profile} onSaved={() => queryClient.invalidateQueries({ queryKey: ["admin-substance-full", id] })} />
      <RoutesEditor substanceId={id} initial={data.routes} onChanged={() => queryClient.invalidateQueries({ queryKey: ["admin-substance-full", id] })} />
      <EffectsEditor substanceId={id} initial={data.effects} onChanged={() => queryClient.invalidateQueries({ queryKey: ["admin-substance-full", id] })} />
    </div>
  );
}

// ---------- PROFILE ----------

function ProfileForm({ substanceId, initial, onSaved }: { substanceId: string; initial: any; onSaved: () => void }) {
  const doSave = useServerFn(upsertSubstanceProfile);
  const [form, setForm] = useState({
    summary: initial?.summary ?? "",
    dose_note: initial?.dose_note ?? "",
    general_notes: initial?.general_notes ?? "",
    tolerance_notes: initial?.tolerance_notes ?? "",
    risk_notes: initial?.risk_notes ?? "",
    harm_reduction: initial?.harm_reduction ?? "",
    confidence: initial?.confidence ?? "medium",
    published: initial?.published ?? false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof typeof form) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await doSave({ data: { substance_id: substanceId, ...form } as any });
      toast.success("Profil gespeichert");
      onSaved();
    } catch (e: any) {
      toast.error("Fehler", { description: e?.message ?? String(e) });
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={save} style={section}>
      <h2 style={{ color: "var(--accent-cyan)", fontSize: "1.05rem", margin: 0 }}>Profil</h2>
      <div>
        <label style={label}>Kurzbeschreibung (DE)</label>
        <textarea rows={2} value={form.summary} onChange={set("summary")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div>
        <label style={label}>Dosis-Hinweis (qualitativ, keine mg-Angaben)</label>
        <textarea rows={2} value={form.dose_note} onChange={set("dose_note")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div>
        <label style={label}>Allgemeine Notizen</label>
        <textarea rows={3} value={form.general_notes} onChange={set("general_notes")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div>
        <label style={label}>Toleranz</label>
        <textarea rows={2} value={form.tolerance_notes} onChange={set("tolerance_notes")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div>
        <label style={label}>Risiken</label>
        <textarea rows={3} value={form.risk_notes} onChange={set("risk_notes")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div>
        <label style={label}>Harm Reduction</label>
        <textarea rows={3} value={form.harm_reduction} onChange={set("harm_reduction")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={label}>Konfidenz</label>
          <select value={form.confidence} onChange={set("confidence")} style={input}>
            {CONFIDENCE_LEVELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "end", gap: 6, color: "var(--text-primary)", fontSize: "0.85rem" }}>
          <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
          Veroeffentlicht
        </label>
      </div>
      <button type="submit" disabled={saving} style={btn}><Save size={14} /> {saving ? "Speichere…" : "Profil speichern"}</button>
    </form>
  );
}

// ---------- ROUTES ----------

function RoutesEditor({ substanceId, initial, onChanged }: { substanceId: string; initial: any[]; onChanged: () => void }) {
  const doSave = useServerFn(upsertSubstanceRoute);
  const doDel = useServerFn(deleteSubstanceRoute);
  const [rows, setRows] = useState(initial);
  const [adding, setAdding] = useState(false);
  useEffect(() => { setRows(initial); }, [initial]);

  async function save(r: any) {
    try {
      const payload = {
        substance_id: substanceId,
        route: r.route,
        bioavailability_pct: r.bioavailability_pct,
        onset_min_minutes: r.onset_min_minutes,
        onset_max_minutes: r.onset_max_minutes,
        duration_min_minutes: r.duration_min_minutes,
        duration_max_minutes: r.duration_max_minutes,
        after_effects_min_minutes: r.after_effects_min_minutes,
        after_effects_max_minutes: r.after_effects_max_minutes,
        notes: r.notes ?? null,
        notes_en: r.notes_en ?? null,
        sort_order: r.sort_order ?? 0,
      };
      await doSave({ data: { id: r.id ?? null, data: payload } as any });
      toast.success("Route gespeichert");
      onChanged();
    } catch (e: any) { toast.error("Fehler", { description: e?.message }); }
  }
  async function del(id: number) {
    if (!confirm("Route loeschen?")) return;
    try { await doDel({ data: { id } }); toast.success("Geloescht"); onChanged(); }
    catch (e: any) { toast.error("Fehler", { description: e?.message }); }
  }

  return (
    <div style={section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ color: "var(--accent-cyan)", fontSize: "1.05rem", margin: 0 }}>Routes (Applikationswege)</h2>
        <button onClick={() => setAdding(true)} style={btn}><Plus size={14} /> Neue Route</button>
      </div>
      {adding && (
        <RouteRow
          row={{ route: "oral", sort_order: rows.length }}
          onSave={(r) => { save(r); setAdding(false); }}
          onCancel={() => setAdding(false)}
        />
      )}
      {rows.length === 0 && !adding && <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: 0 }}>Noch keine Routes.</p>}
      {rows.map((r) => <RouteRow key={r.id} row={r} onSave={save} onDelete={() => del(r.id)} />)}
    </div>
  );
}

function RouteRow({ row, onSave, onDelete, onCancel }: { row: any; onSave: (r: any) => void; onDelete?: () => void; onCancel?: () => void }) {
  const [r, setR] = useState(row);
  const num = (v: any) => v === "" || v == null ? null : Number(v);
  const set = (k: string) => (e: any) => setR((x: any) => ({ ...x, [k]: e.target.value }));
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "0.75rem", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 8 }}>
        <div>
          <label style={label}>Route</label>
          <select value={r.route} onChange={set("route")} style={input}>
            {ROA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div><label style={label}>Bioverf. (%)</label><input value={r.bioavailability_pct ?? ""} onChange={set("bioavailability_pct")} style={input} /></div>
        <div><label style={label}>Sort.</label><input value={r.sort_order ?? 0} onChange={set("sort_order")} style={input} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
        <div><label style={label}>Onset min (min)</label><input value={r.onset_min_minutes ?? ""} onChange={set("onset_min_minutes")} style={input} /></div>
        <div><label style={label}>Onset max (min)</label><input value={r.onset_max_minutes ?? ""} onChange={set("onset_max_minutes")} style={input} /></div>
        <div><label style={label}>Dauer min (min)</label><input value={r.duration_min_minutes ?? ""} onChange={set("duration_min_minutes")} style={input} /></div>
        <div><label style={label}>Dauer max (min)</label><input value={r.duration_max_minutes ?? ""} onChange={set("duration_max_minutes")} style={input} /></div>
      </div>
      <div>
        <label style={label}>Notiz (qualitativ)</label>
        <textarea rows={2} value={r.notes ?? ""} onChange={set("notes")} style={{ ...input, resize: "vertical" }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => onSave({ ...r,
          bioavailability_pct: num(r.bioavailability_pct),
          onset_min_minutes: num(r.onset_min_minutes),
          onset_max_minutes: num(r.onset_max_minutes),
          duration_min_minutes: num(r.duration_min_minutes),
          duration_max_minutes: num(r.duration_max_minutes),
        })} style={btn}><Save size={12} /> Speichern</button>
        {onDelete && <button onClick={onDelete} style={{ ...btn, background: "transparent", color: "#ef4444", border: "1px solid #ef444455" }}><Trash2 size={12} /> Loeschen</button>}
        {onCancel && <button onClick={onCancel} style={{ ...btn, background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border-purple)" }}>Abbrechen</button>}
      </div>
    </div>
  );
}

// ---------- EFFECTS ----------

function EffectsEditor({ substanceId, initial, onChanged }: { substanceId: string; initial: any[]; onChanged: () => void }) {
  const doSave = useServerFn(upsertSubstanceEffect);
  const doDel = useServerFn(deleteSubstanceEffect);
  const [adding, setAdding] = useState(false);
  const desired = useMemo(() => initial.filter((e) => e.kind === "desired"), [initial]);
  const adverse = useMemo(() => initial.filter((e) => e.kind === "adverse"), [initial]);
  const neutral = useMemo(() => initial.filter((e) => e.kind === "neutral"), [initial]);

  async function save(eff: any) {
    try {
      await doSave({ data: {
        id: eff.id ?? null,
        data: { substance_id: substanceId, kind: eff.kind, text: eff.text, text_en: eff.text_en ?? null, sort_order: eff.sort_order ?? 0 },
      } as any });
      toast.success("Effekt gespeichert");
      onChanged();
    } catch (e: any) { toast.error("Fehler", { description: e?.message }); }
  }
  async function del(id: number) {
    if (!confirm("Effekt loeschen?")) return;
    try { await doDel({ data: { id } }); toast.success("Geloescht"); onChanged(); }
    catch (e: any) { toast.error("Fehler", { description: e?.message }); }
  }

  return (
    <div style={section}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ color: "var(--accent-cyan)", fontSize: "1.05rem", margin: 0 }}>Effekte</h2>
        <button onClick={() => setAdding(true)} style={btn}><Plus size={14} /> Neuer Effekt</button>
      </div>
      {adding && (
        <EffectRow row={{ kind: "desired", text: "", sort_order: 0 }} onSave={(e) => { save(e); setAdding(false); }} onCancel={() => setAdding(false)} />
      )}
      {[["Erwuenscht", desired], ["Neutral", neutral], ["Nebenwirkungen", adverse]].map(([title, list]: any) => (
        <div key={title}>
          <div style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 6 }}>{title}</div>
          {list.length === 0 ? <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>—</p> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {list.map((e: any) => <EffectRow key={e.id} row={e} onSave={save} onDelete={() => del(e.id)} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EffectRow({ row, onSave, onDelete, onCancel }: { row: any; onSave: (e: any) => void; onDelete?: () => void; onCancel?: () => void }) {
  const [e, setE] = useState(row);
  const set = (k: string) => (ev: any) => setE((x: any) => ({ ...x, [k]: ev.target.value }));
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "0.6rem", display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div style={{ minWidth: 110 }}>
        <label style={label}>Art</label>
        <select value={e.kind} onChange={set("kind")} style={input}>
          {EFFECT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <label style={label}>Text (DE)</label>
        <input value={e.text} onChange={set("text")} style={input} />
      </div>
      <div style={{ width: 80 }}>
        <label style={label}>Sort.</label>
        <input value={e.sort_order ?? 0} onChange={set("sort_order")} style={input} />
      </div>
      <button onClick={() => onSave(e)} style={btn}><Save size={12} /></button>
      {onDelete && <button onClick={onDelete} style={{ ...btn, background: "transparent", color: "#ef4444", border: "1px solid #ef444455" }}><Trash2 size={12} /></button>}
      {onCancel && <button onClick={onCancel} style={{ ...btn, background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border-purple)" }}>Abbrechen</button>}
    </div>
  );
}
